import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Card,
  CardContent,
  CardActions,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  Snackbar,
  Divider,
  Autocomplete
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Business as BusinessIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  People as PeopleIcon,
  CloudUpload as CloudUploadIcon,
  Image as ImageIcon
} from '@mui/icons-material';
import { useData } from '../../context/DataContext';
import { v4 as uuidv4 } from 'uuid';
import RichTextEditor from '../common/RichTextEditor';

const iconOptions = [
  { value: 'emergency', label: 'Emergency' },
  { value: 'favorite', label: 'Heart/Cardiology' },
  { value: 'psychology', label: 'Psychology/Neurology' },
  { value: 'child_care', label: 'Pediatrics' },
  { value: 'accessibility', label: 'Orthopedics' },
  { value: 'healing', label: 'Oncology' },
  { value: 'local_hospital', label: 'General Hospital' },
  { value: 'medical_services', label: 'Medical Services' },
  { value: 'stethoscope', label: 'General Medicine' },
  { value: 'monitor_heart', label: 'Cardiac Monitoring' },
  { value: 'brain', label: 'Neurology' },
  { value: 'elderly', label: 'Geriatrics' },
  { value: 'pregnant_woman', label: 'Obstetrics' },
  { value: 'visibility', label: 'Ophthalmology' },
  { value: 'hearing', label: 'ENT' },
  { value: 'dentistry', label: 'Dental Care' }
];

const ManageDepartments = () => {
  const { state, dispatch } = useData();
  const { departments, doctors } = state;
  const [openDialog, setOpenDialog] = useState(false);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [searchQuery, setSearchQuery] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    headOfDepartment: null, // Will store doctor object
    phone: '',
    icon: '',
    iconFile: null,
    iconPreview: null,
    isActive: true,
    serviceCategory: '',
    departmentInfo: '',
    expertise: '',
    services: '',
    heroImage: null,
    heroImagePreview: null,
    showPhone: true,
    showHeadOfDepartment: true
  });

  const serviceCategories = [
    'Medical Clinics',
    'Surgical Clinics',
    'Diagnostic Services',
    'Emergency Services'
  ];

  const handleOpenDialog = (department = null) => {
    if (department) {
      setEditingDepartment(department);
      // Find the doctor object from the doctors array
      const selectedDoctor = doctors.find(doc => doc.id === department.head_of_department_id) || null;
      
      setFormData({
        name: department.name,
        description: department.description,
        headOfDepartment: selectedDoctor,
        phone: department.phone,
        icon: department.icon || '', // This will be the icon image ID if uploaded
        iconFile: null,
        iconPreview: department.icon && !['emergency', 'favorite', 'psychology', 'child_care', 'accessibility', 'healing', 'local_hospital', 'medical_services', 'stethoscope'].includes(department.icon) ? `https://${window.location.hostname}/api/images/${department.icon}` : null,
        isActive: department.is_active === 1 || department.is_active === true,
        serviceCategory: department.service_category || '',
        departmentInfo: department.department_info || '',
        expertise: department.expertise || '',
        services: department.services || '',
        heroImage: null,
        heroImagePreview: department.hero_image_id ? `https://${window.location.hostname}/api/images/${department.hero_image_id}` : null,
        showPhone: department.show_phone === 1 || department.show_phone === true,
        showHeadOfDepartment: department.show_head_of_department === 1 || department.show_head_of_department === true
      });
    } else {
      setEditingDepartment(null);
      setFormData({
        name: '',
        description: '',
        headOfDepartment: null,
        phone: '',
        icon: '',
        iconFile: null,
        iconPreview: null,
        isActive: true,
        serviceCategory: '',
        departmentInfo: '',
        expertise: '',
        services: '',
        heroImage: null,
        heroImagePreview: null,
        showPhone: true,
        showHeadOfDepartment: true
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingDepartment(null);
  };

  const handleInputChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value
    });
  };

  const handleSwitchChange = (event) => {
    setFormData({
      ...formData,
      isActive: event.target.checked
    });
  };

  const handleToggleChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.checked
    });
  };

  const handleDoctorChange = (event, newValue) => {
    setFormData({
      ...formData,
      headOfDepartment: newValue
    });
  };

  const handleHeroImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
      if (!validTypes.includes(file.type)) {
        setSnackbar({
          open: true,
          message: 'Please select a valid image file (JPEG, PNG, or GIF)',
          severity: 'error'
        });
        return;
      }

      // Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        setSnackbar({
          open: true,
          message: 'Image size must be less than 5MB',
          severity: 'error'
        });
        return;
      }

      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      
      setFormData({
        ...formData,
        heroImage: file,
        heroImagePreview: previewUrl
      });
    }
  };

  const handleIconImageUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif'];
      if (!validTypes.includes(file.type)) {
        setSnackbar({
          open: true,
          message: 'Please select a valid image file (JPEG, PNG, or GIF)',
          severity: 'error'
        });
        return;
      }

      // Validate file size (max 2MB for icons)
      const maxSize = 2 * 1024 * 1024; // 2MB
      if (file.size > maxSize) {
        setSnackbar({
          open: true,
          message: 'Icon size must be less than 2MB',
          severity: 'error'
        });
        return;
      }

      // Create preview URL
      const previewUrl = URL.createObjectURL(file);
      
      setFormData({
        ...formData,
        iconFile: file,
        iconPreview: previewUrl
      });
    }
  };

  const handleSubmit = async () => {
    // All fields are now optional, no validation needed

    const heroImageFile = formData.heroImage;
    const iconFile = formData.iconFile;
    
    const departmentData = {
      ...formData,
      headOfDepartmentId: formData.headOfDepartment?.id, // Store doctor ID
      headOfDepartmentName: formData.headOfDepartment?.name, // Store doctor name for display
      id: editingDepartment ? editingDepartment.id : uuidv4()
    };
    
    // For new departments, don't send empty icon field
    if (!editingDepartment && !departmentData.icon) {
      delete departmentData.icon;
    }

    // If editing and not uploading new image, include existing hero image ID
    if (editingDepartment && !heroImageFile && editingDepartment.hero_image_id) {
      departmentData.existingHeroImageId = editingDepartment.hero_image_id;
    }

    // If editing and not uploading new icon image, include existing icon ID  
    if (editingDepartment && !iconFile) {
      // Get current icon if it exists (which might be an image ID)
      if (editingDepartment.icon) {
        departmentData.existingIconImageId = editingDepartment.icon;
      }
    }

    // Remove fields that shouldn't be sent to backend
    // Keep icon field as it might be used for legacy icon selection
    delete departmentData.headOfDepartment;
    delete departmentData.heroImage;
    delete departmentData.heroImagePreview;
    delete departmentData.iconFile;
    delete departmentData.iconPreview;

    try {
      let result;
      if (editingDepartment) {
        result = await dispatch({ 
          type: 'UPDATE_DEPARTMENT', 
          payload: departmentData,
          heroImageFile: heroImageFile,
          iconFile: iconFile
        });
      } else {
        result = await dispatch({ 
          type: 'ADD_DEPARTMENT', 
          payload: departmentData,
          heroImageFile: heroImageFile,
          iconFile: iconFile
        });
      }

      if (result.success) {
        setSnackbar({
          open: true,
          message: editingDepartment ? 'Department updated successfully' : 'Department added successfully',
          severity: 'success'
        });
        handleCloseDialog();
      } else {
        setSnackbar({
          open: true,
          message: `Failed to ${editingDepartment ? 'update' : 'add'} department: ${result.error}`,
          severity: 'error'
        });
      }
    } catch (error) {
      console.error(`Error ${editingDepartment ? 'updating' : 'adding'} department:`, error);
      setSnackbar({
        open: true,
        message: `Error ${editingDepartment ? 'updating' : 'adding'} department: ${error.message}`,
        severity: 'error'
      });
    }
  };

  const handleDelete = async (departmentId) => {
    if (window.confirm('Are you sure you want to delete this department?')) {
      try {
        const result = await dispatch({ type: 'DELETE_DEPARTMENT', payload: departmentId });
        if (result.success) {
          setSnackbar({
            open: true,
            message: 'Department deleted successfully',
            severity: 'success'
          });
        } else {
          setSnackbar({
            open: true,
            message: `Failed to delete department: ${result.error}`,
            severity: 'error'
          });
        }
      } catch (error) {
        console.error('Error deleting department:', error);
        setSnackbar({
          open: true,
          message: `Error deleting department: ${error.message}`,
          severity: 'error'
        });
      }
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
          Manage Departments
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            bgcolor: '#2563eb',
            '&:hover': { bgcolor: '#1d4ed8' }
          }}
        >
          Add Department
        </Button>
      </Box>

      {/* Search Box */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search departments by name, description, or service category..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          variant="outlined"
        />
      </Box>

      <Grid container spacing={3}>
        {(() => {
          // Filter departments based on search query
          const filteredDepartments = searchQuery.trim() === ''
            ? state.departments
            : state.departments.filter(department =>
                department.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                department.description?.toLowerCase().includes(searchQuery.toLowerCase()) ||
                department.service_category?.toLowerCase().includes(searchQuery.toLowerCase())
              );

          return filteredDepartments.map((department) => (
          <Grid item xs={12} md={6} lg={4} key={department.id}>
            <Card sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              '&:hover': {
                boxShadow: '0px 8px 25px rgba(0,0,0,0.15)',
                transform: 'translateY(-2px)',
              },
              transition: 'all 0.3s ease',
            }}>
              <CardContent sx={{ flexGrow: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography variant="h6" component="h2" sx={{ fontWeight: 600, color: '#2563eb' }}>
                    {department.name}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                    <Chip
                      label={department.is_active || department.is_active === 1 ? 'Active' : 'Inactive'}
                      color={department.is_active || department.is_active === 1 ? 'success' : 'default'}
                      size="small"
                    />
                    {department.service_category && (
                      <Chip
                        label={department.service_category}
                        color="primary"
                        size="small"
                        variant="outlined"
                      />
                    )}
                  </Box>
                </Box>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {department.description}
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {(department.show_head_of_department !== false && department.show_head_of_department !== 0) && department.head_of_department_name && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <BusinessIcon sx={{ fontSize: 16, color: '#6b7280' }} />
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {department.head_of_department_name}
                      </Typography>
                    </Box>
                  )}

                  {(department.show_phone !== false && department.show_phone !== 0) && department.phone && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PhoneIcon sx={{ fontSize: 16, color: '#6b7280' }} />
                      <Typography variant="body2">
                        {department.phone}
                      </Typography>
                    </Box>
                  )}


                </Box>
              </CardContent>

              <CardActions sx={{ justifyContent: 'flex-end', p: 2, pt: 0 }}>
                <IconButton
                  onClick={() => handleOpenDialog(department)}
                  sx={{ color: '#2563eb' }}
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  onClick={() => handleDelete(department.id)}
                  sx={{ color: '#dc2626' }}
                >
                  <DeleteIcon />
                </IconButton>
              </CardActions>
            </Card>
          </Grid>
          ));
        })()}
      </Grid>

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ bgcolor: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
          {editingDepartment ? 'Edit Department' : 'Add New Department'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={3}>
            {/* Basic Information Section */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#2563eb' }}>
                Basic Information
              </Typography>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Department Name"
                value={formData.name}
                onChange={handleInputChange('name')}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={doctors || []}
                getOptionLabel={(option) => option ? `${option.name} (${option.specialty})` : ''}
                value={formData.headOfDepartment}
                onChange={handleDoctorChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    label="Head of Department"
                    helperText="Search and select a doctor from the list"
                  />
                )}
                isOptionEqualToValue={(option, value) => option?.id === value?.id}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body1">
                        {option.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {option.specialty} • {option.experience} experience
                      </Typography>
                    </Box>
                  </Box>
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={formData.description}
                onChange={handleInputChange('description')}
                multiline
                rows={3}
                helperText="Brief description of the department"
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone"
                value={formData.phone}
                onChange={handleInputChange('phone')}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Hero Image (Recommended: 1920x600px)
                </Typography>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="hero-image-upload"
                  type="file"
                  onChange={handleHeroImageUpload}
                />
                <label htmlFor="hero-image-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CloudUploadIcon />}
                    fullWidth
                    sx={{ mb: 1 }}
                  >
                    Upload Hero Image
                  </Button>
                </label>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                  Recommended dimensions: 1920x600px. PNG/JPEG uploads are optimized and saved as WebP.
                </Typography>
                {formData.heroImagePreview && (
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <img
                      src={formData.heroImagePreview}
                      alt="Hero Preview"
                      style={{
                        maxWidth: '100%',
                        maxHeight: '200px',
                        objectFit: 'cover',
                        borderRadius: '8px',
                        border: '1px solid #e0e0e0'
                      }}
                    />
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Department Icon (Recommended: 36x36px)
                </Typography>
                <input
                  accept="image/*"
                  style={{ display: 'none' }}
                  id="icon-image-upload"
                  type="file"
                  onChange={handleIconImageUpload}
                />
                <label htmlFor="icon-image-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<CloudUploadIcon />}
                    fullWidth
                    sx={{ mb: 1 }}
                  >
                    Upload Icon Image
                  </Button>
                </label>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 2 }}>
                  Recommended dimensions: 100x100px (square). Leave empty to use default icon
                </Typography>
                {formData.iconPreview && (
                  <Box sx={{ mt: 2, textAlign: 'center' }}>
                    <img
                      src={formData.iconPreview}
                      alt="Icon Preview"
                      style={{
                        maxWidth: '100px',
                        maxHeight: '100px',
                        objectFit: 'cover',
                        borderRadius: '8px',
                        border: '1px solid #e0e0e0'
                      }}
                    />
                  </Box>
                )}
              </Box>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Service Category</InputLabel>
                <Select
                  value={formData.serviceCategory}
                  onChange={handleInputChange('serviceCategory')}
                  label="Service Category"
                >
                  {serviceCategories.map((category) => (
                    <MenuItem key={category} value={category}>
                      {category}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>

            {/* Public Display Settings */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#2563eb' }}>
                Public Display Settings
              </Typography>
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.showHeadOfDepartment}
                    onChange={(e) => setFormData({...formData, showHeadOfDepartment: e.target.checked})}
                    color="primary"
                  />
                }
                label="Show Head of Department"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.showPhone}
                    onChange={(e) => setFormData({...formData, showPhone: e.target.checked})}
                    color="primary"
                  />
                }
                label="Show Phone Number"
              />
            </Grid>


            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
            </Grid>

            {/* Department Details Section */}
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: '#2563eb' }}>
                Department Details (for Public Pages)
              </Typography>
            </Grid>

            <Grid item xs={12}>
              <RichTextEditor
                label="Department Information"
                value={formData.departmentInfo}
                onChange={(value) => setFormData(prev => ({ ...prev, departmentInfo: value }))}
                placeholder="Our Cardiology Department provides comprehensive cardiovascular care using state-of-the-art technology and evidence-based treatments..."
                rows={4}
                helperText="Detailed information about the department that will be displayed on the public department page. Use the toolbar above to format your text."
              />
            </Grid>

            <Grid item xs={12}>
              <RichTextEditor
                label="Our Expertise"
                value={formData.expertise}
                onChange={(value) => setFormData(prev => ({ ...prev, expertise: value }))}
                placeholder="Our cardiologists are board-certified specialists with expertise in interventional cardiology, electrophysiology, and heart failure management."
                rows={3}
                helperText="Description of the department's expertise and specializations. Use the toolbar above to format your text."
              />
            </Grid>

            <Grid item xs={12}>
              <RichTextEditor
                label="Services We Offer"
                value={formData.services}
                onChange={(value) => setFormData(prev => ({ ...prev, services: value }))}
                placeholder="Cardiac catheterization and angioplasty&#10;Electrocardiogram (ECG) and stress testing&#10;Echocardiography and cardiac imaging&#10;Pacemaker and defibrillator implantation&#10;Cardiac rehabilitation programs&#10;Preventive cardiology and risk assessment"
                rows={4}
                helperText="List of services offered by the department. Use the toolbar above to format your text with lists, bold, italic, etc."
              />
            </Grid>

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isActive}
                    onChange={handleSwitchChange}
                    color="primary"
                  />
                }
                label="Department Active"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, bgcolor: '#f8fafc' }}>
          <Button onClick={handleCloseDialog} sx={{ color: '#6b7280' }}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            sx={{
              bgcolor: '#2563eb',
              '&:hover': { bgcolor: '#1d4ed8' }
            }}
          >
            {editingDepartment ? 'Update' : 'Add'} Department
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ManageDepartments; 